import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RowDrawerBarcodesModel as Barcodes } from './rowDrawerResolutions';

type RowDrawerBarcodesProps = {
    barcodes: Barcodes;
    onMakeMain: (rowNumber: number) => void;
};

/**
 * The barcodes a new variant keeps (`BARCODES_KEPT_AS_ALTERNATES`,
 * bos-app#219), as a card under the row's notes: the title and that every
 * one scans at the till, then the main barcode with its chip ("Main · most
 * stock", or "Main · your pick" after a Make main), then each extra with
 * **Make main**, named for its barcode for screen readers. An extra no line
 * of the variant carries any more (the row was edited since the check)
 * offers none.
 */
export const RowDrawerBarcodes: React.FC<RowDrawerBarcodesProps> = ({
    barcodes,
    onMakeMain,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Card
            className="w-full gap-3"
            variant="secondary"
            {...getNodeTestIdProp('catalog-import-row-drawer-barcodes')}
        >
            <Card.Header className="gap-1">
                <Card.Title>
                    {t('catalogImport.products.drawer.barcodes.title')}
                </Card.Title>
                <Card.Description>
                    {t('catalogImport.products.drawer.barcodes.intro')}
                </Card.Description>
            </Card.Header>
            <Card.Content>
                <ul className="flex flex-col gap-2">
                    <li
                        className="flex min-h-8 flex-wrap items-center gap-2"
                        {...getNodeTestIdProp(
                            'catalog-import-row-drawer-barcode-main',
                        )}
                    >
                        <Typography
                            className="min-w-0 flex-1 font-mono break-all"
                            type="body-sm"
                        >
                            {barcodes.main}
                        </Typography>
                        <Chip color="success" size="sm" variant="soft">
                            {t(
                                barcodes.isPicked
                                    ? 'catalogImport.products.drawer.barcodes.mainPicked'
                                    : 'catalogImport.products.drawer.barcodes.mainMostStock',
                            )}
                        </Chip>
                    </li>
                    {barcodes.extras.map((extra) => (
                        <li
                            className="flex min-h-8 flex-wrap items-center gap-2"
                            key={extra.barcode}
                            {...getNodeTestIdProp(
                                `catalog-import-row-drawer-barcode-extra-${extra.barcode}`,
                            )}
                        >
                            <Typography
                                className="min-w-0 flex-1 font-mono break-all"
                                type="body-sm"
                            >
                                {extra.barcode}
                            </Typography>
                            <Typography color="muted" type="body-xs">
                                {t(
                                    'catalogImport.products.drawer.barcodes.extra',
                                )}
                            </Typography>
                            {extra.rowNumber === null ? null : (
                                <Button
                                    aria-label={t(
                                        'catalogImport.products.drawer.barcodes.makeMainAria',
                                        { barcode: extra.barcode },
                                    )}
                                    onPress={() => {
                                        if (extra.rowNumber !== null) {
                                            onMakeMain(extra.rowNumber);
                                        }
                                    }}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `catalog-import-row-drawer-make-main-${extra.barcode}`,
                                    )}
                                >
                                    {t(
                                        'catalogImport.products.drawer.barcodes.makeMain',
                                    )}
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            </Card.Content>
        </Card>
    );
};
